// ----------------------------------------------------------------
// <copyright company='Microsoft Corporation'>
//     Copyright (c) Microsoft Corporation.  All rights reserved.
// </copyright>
// ----------------------------------------------------------------

import clsx from 'clsx';
import { saveLoadFactory, SaveLocation } from 'application-storage';
import { DefaultButton } from 'office-ui-fabric-react';
import * as React from 'react';
import { useTranslation } from 'react-i18next';

import URLS from './../../constants/urls';
import { logPageAction } from './../../lib/ApplicationInsights';

import Styles from './productSafetyForm.module.scss';
import ImgModal from './../../components/ImgModal';

function Body() {
    const { t } = useTranslation();
    const dataKey = 'productSafetyForm';
    const appStorage = saveLoadFactory(dataKey, SaveLocation.LocalStorage);
    const data: any = appStorage.getValue();
    appStorage.removeValue();

    if (!data) {
        return (
            <div>{t('error_occurred')}</div>
        );
    }
    return (
        <div className={Styles.body}>
            <div className={Styles.title}>{t('product_safety_form')}</div>
            <section className={Styles.section}>
                <BodyLeft data={data} />
            </section>
        </div>
    );
}

function BodyLeft({ data }:
    {
        data: any
    }) {
    const { t } = useTranslation();
    const { safetyIssueInfo, deviceInfo, serviceRequestId,symptomDescription, comments, createdDate, claimOrderId, userInfo } = data;
    const { deviceDateOfPurchase, partnerLocationInfo, attachments } = safetyIssueInfo;
    const { addressLine1,
        addressLine2,
        city,
        postalCode,
        country } = partnerLocationInfo;
    const { productFamilyName, serialNumber } = deviceInfo;
    const formatedCreatedDate = new Intl.DateTimeFormat('en-US', { year: 'numeric', month: '2-digit', day: '2-digit' }).
        format(new Date(createdDate.toString()));
    const formatedDeviceDateOfPurchase = deviceDateOfPurchase ? new Intl.DateTimeFormat('en-US', { year: 'numeric', month: '2-digit', day: '2-digit' }).
        format(new Date(deviceDateOfPurchase)) : '';

    return (
        <div className={Styles.bodyLeft}>
            <RenderDetail label={t('partner_name')} value={userInfo.soldtoCustomerName} />
            <RenderDetail label={t('submitted_date')} value={formatedCreatedDate} />
            <RenderDetail label={t('device_type')} value={deviceInfo.localizedName} />
            <RenderDetail label={t('device_serial_number')} value={serialNumber} />
            <RenderDetail label={t('service_request_number')} value={serviceRequestId} />
            <RenderDetail label={t('device_sku')} value={claimOrderId} />
            <RenderDetail label={`${t('device_model')} (${t('product_family')})`} value={productFamilyName} />
            <RenderDetail label={t('product_type')} value={`${t(symptomDescription)}`} />
            <RenderDetail label={t('service_agent_inspection_notes')} value={comments} />
            <RenderDetail label={`${t('device_date_of_purchase')} (${t('optional')})`} value={formatedDeviceDateOfPurchase} />
            <RenderDetail label={t('country')} value={`${country}`} />
            <div className={Styles.address}>
                <label className={Styles.label} htmlFor={t('partner_location_address')}>
                    {`${t('partner_location_address')}`}:</label>
                <span>
                    <div>{addressLine1}</div>
                    <div>{addressLine2}</div>
                    <div>{`${city} ${country}`}</div>
                    <div>{`${postalCode}`}</div>
                </span>
            </div>
            <div className={Styles.attachments}>
                <div>
                    <label className={Styles.label} htmlFor={t('attachments')}>
                        {`${t('attachments')} 
                    (${t('photos_of_device')})`}:</label>
                </div>
                {
                    attachments && attachments.map((image: any, i: number) => {
                        return <span key={i}>
                            <span>
                                <ImgModal imageSrc={image.contentData} thumbnailSrc={image.contentData} alt={t('Image')} className={Styles.image}/>
                                
                            </span>
                        </span>;
                    })
                }
            </div>
        </div >
    );
}

function Header() {
    const { t } = useTranslation();

    const privacyStatementUrl = URLS.privacy_statement;

    const onClose = () => {
        logPageAction('Close button clicked');

        window.close();
    };

    const onPrint = () => {
        logPageAction('Print receipt button clicked');

        window.print();
    };


    return (
        <header className={Styles.header}>
            <div className={Styles.topHeader}>
                <DefaultButton
                    className={Styles.closeButton}
                    text={t('close')}
                    onClick={onClose}
                />
                <DefaultButton
                    className={Styles.printButton}
                    text={t('print')}
                    onClick={onPrint}
                />
            </div>
            <div className={Styles.bottomHeader}>
                {t('review_microsoft')}&nbsp;
                <a href={privacyStatementUrl} target="_blank">
                    {t('privacy_statement')}
                </a>
            </div>
        </header>
    );
}

export default function ProductSafetyForm() {
    return (
        <div className={Styles.main}>
            <Header />
            <Body />
        </div>
    );
}

function RenderDetail({ label, value, children, noMargin }: any) {
    const className = clsx({
        [Styles.detail]: !noMargin,
    });
    return (
        <div className={className}>
            <div className={Styles.label}>
                {label}:
                <span className={Styles.value}>&nbsp;{value}</span>
            </div>
            {children}
        </div>
    );
}